import { getCurrentLocale, uiText } from '../../utils/i18n';
import { useMemo, useState } from 'react';
import type { ChatMessage, FileAttachment } from '../../types';
import { formatFileSize, fileExt, isTextPreviewable, isEmbedPreviewable } from './helpers';
import styles from './FilesPanel.module.css';
import { AuthenticatedImage } from '../shared/AuthenticatedImage';
import { downloadAuthenticatedFile } from '../../hooks/useAuthenticatedBlobUrl';

type ViewMode = 'list' | 'grid';

interface ChatFileEntry {
  file: FileAttachment;
  timestamp: number;
  sender: string;
}

interface ChatFilesPanelProps {
  messages: ChatMessage[];
  botName: string;
  onFilePreview?: (file: FileAttachment) => void;
}

function getExtAccent(ext: string): string {
  const map: Record<string, string> = {
    html: '#c54a2f', htm: '#c54a2f',
    md: '#5a6a2a', markdown: '#5a6a2a', txt: '#888',
    json: '#7850c8', xml: '#7850c8', yaml: '#7850c8', yml: '#7850c8',
    js: '#c79a3a', ts: '#5a8ab8', jsx: '#c79a3a', tsx: '#5a8ab8',
    py: '#5a8ab8', css: '#5a8ab8',
    csv: '#c79a3a', xlsx: '#2a8a3c', xls: '#2a8a3c',
    pdf: '#5a8ab8',
    docx: '#3a6acc', doc: '#3a6acc',
    pptx: '#d2641e', ppt: '#d2641e',
    zip: '#a07828', tar: '#a07828', gz: '#a07828',
    png: '#3a8a6a', jpg: '#3a8a6a', jpeg: '#3a8a6a', gif: '#3a8a6a', svg: '#3a8a6a', webp: '#3a8a6a',
    mp3: '#8a3a8a', mp4: '#8a3a8a', mov: '#8a3a8a',
    sh: '#555', log: '#888',
  };
  return map[ext] || '#5a6a2a';
}

function canPreview(file: FileAttachment): boolean {
  const ext = fileExt(file.name);
  return isTextPreviewable(file.name, file.type) || isEmbedPreviewable(file.name) || ext === 'pdf' || file.type?.startsWith('image/');
}

function formatTime(ts: number): string {
  const d = new Date(ts);
  const now = new Date();
  const locale = getCurrentLocale();
  const relative = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const time = d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });

  if (d.toDateString() === now.toDateString()) return `${relative.format(0, 'day')} ${time}`;
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return `${relative.format(-1, 'day')} ${time}`;
  const date = new Intl.DateTimeFormat(locale, {
    year: d.getFullYear() === now.getFullYear() ? undefined : 'numeric',
    month: 'short', day: 'numeric',
  }).format(d);
  return `${date} ${time}`;
}

/** Paper document icon with folded corner, halftone, text lines, colored ext tag */
function FileDocIcon({ ext, accent, isImage, url, large }: { ext: string; accent: string; isImage: boolean; url: string; large?: boolean }) {
  const cls = large ? `${styles.fileDocIcon} ${styles.fileDocIconLg}` : styles.fileDocIcon;
  if (isImage) {
    return (
      <div className={cls}>
        <AuthenticatedImage src={url} alt="" className={styles.fileDocThumb} loading="lazy" />
      </div>
    );
  }
  return (
    <div className={cls}>
      <div className={styles.fileDocBody}>
        <div className={styles.fileDocLines}>
          {[100, 80, 90, 50].map((w, i) => (
            <div key={i} className={styles.fileDocLine} style={{ width: `${w}%` }} />
          ))}
        </div>
        <div className={styles.fileDocCorner} />
      </div>
      <span className={styles.fileDocExt} style={{ background: accent }}>{ext}</span>
    </div>
  );
}

export function ChatFilesPanel({ messages, botName, onFilePreview }: ChatFilesPanelProps) {
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('list');

  const files = useMemo(() => {
    const result: ChatFileEntry[] = [];
    for (const msg of messages) {
      if (msg.type === 'assistant' && msg.attachments?.length) {
        const sender = msg.botName || botName;
        for (const file of msg.attachments) {
          result.push({ file, timestamp: msg.timestamp, sender });
        }
      }
    }
    result.reverse();
    return result;
  }, [messages, botName]);

  const filtered = useMemo(() => {
    if (!search.trim()) return files;
    const q = search.trim().toLowerCase();
    return files.filter((f) => f.file.name.toLowerCase().includes(q));
  }, [files, search]);

  const handleClick = (entry: ChatFileEntry) => {
    if (canPreview(entry.file) && onFilePreview) {
      onFilePreview(entry.file);
    } else {
      void downloadAuthenticatedFile(entry.file);
    }
  };

  const handleDownload = (e: React.MouseEvent, file: FileAttachment) => {
    e.stopPropagation();
    void downloadAuthenticatedFile(file);
  };

  if (files.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyWrap}>
          <div className={styles.emptyIconWrap}>
            <svg width={28} height={28} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
            </svg>
          </div>
          <div className={styles.emptyTitle}>{uiText("暂无文件")}</div>
          <div className={styles.emptyHint}>{uiText("Agent 在对话中输出的文件会显示在这里")}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Toolbar: search + view toggle */}
      <div className={styles.toolbar}>
        <div className={styles.searchBar}>
          <svg className={styles.searchIcon} width={18} height={18} viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
            <path d="M16.5 16.5L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <input
            className={styles.searchInput}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={uiText("搜索会话内的文件")}
          />
          {search && (
            <button className={styles.searchClear} onClick={() => setSearch('')}>
              <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
        <div className={styles.viewToggle}>
          <button
            className={`${styles.viewBtn} ${viewMode === 'list' ? styles.viewBtnActive : ''}`}
            onClick={() => setViewMode('list')}
            title={uiText("列表视图")}
          >
            <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="4" y1="6" x2="20" y2="6" />
              <line x1="4" y1="12" x2="20" y2="12" />
              <line x1="4" y1="18" x2="20" y2="18" />
            </svg>
          </button>
          <button
            className={`${styles.viewBtn} ${viewMode === 'grid' ? styles.viewBtnActive : ''}`}
            onClick={() => setViewMode('grid')}
            title={uiText("图标视图")}
          >
            <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7" rx="1.5" />
              <rect x="14" y="3" width="7" height="7" rx="1.5" />
              <rect x="3" y="14" width="7" height="7" rx="1.5" />
              <rect x="14" y="14" width="7" height="7" rx="1.5" />
            </svg>
          </button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className={styles.noResults}>{uiText("没有找到匹配的文件")}</div>
      ) : viewMode === 'list' ? (
        <>
          {/* List view */}
          <div className={styles.tableHeader}>
            <span>{uiText("标题")}</span>
            <span>{uiText("发送人")}</span>
            <span>{uiText("发送时间")}</span>
          </div>
          <div className={styles.tableBody}>
            <div className={styles.tableCard}>
              {filtered.map((entry, i) => {
                const ext = fileExt(entry.file.name);
                const accent = getExtAccent(ext);
                const isImage = entry.file.type?.startsWith('image/') ?? false;
                const nameWithoutExt = entry.file.name.replace(/\.[^.]+$/, '');

                return (
                  <div
                    key={`${entry.file.url}-${i}`}
                    className={styles.fileRow}
                    onClick={() => handleClick(entry)}
                  >
                    <div className={styles.colTitle}>
                      <FileDocIcon ext={ext} accent={accent} isImage={isImage} url={entry.file.url} />
                      <div className={styles.fileTitleGroup}>
                        <span className={styles.fileName}>
                          {nameWithoutExt}
                          <span className={styles.fileNameExt}>.{ext}</span>
                        </span>
                        <span className={styles.fileSize}>{formatFileSize(entry.file.size)}</span>
                      </div>
                    </div>
                    <div className={styles.colSender}>{entry.sender}</div>
                    <div className={styles.colTime}>
                      <span className={styles.timeText}>{formatTime(entry.timestamp)}</span>
                      <button className={styles.dlBtn} title={uiText("下载")} onClick={(e) => handleDownload(e, entry.file)}>
                        <svg width={15} height={15} viewBox="0 0 24 24" fill="none">
                          <path d="M12 4v11M7 11l5 5 5-5M5 20h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Grid view */}
          <div className={styles.gridHeader}>
            <span className={styles.gridHeaderLabel}>{uiText("会话文件")}</span>
            <span className={styles.gridHeaderCount}>&middot; {String(filtered.length).padStart(2, '0')}</span>
            <span className={styles.gridHeaderLine} />
          </div>
          <div className={styles.gridBody}>
            <div className={styles.grid}>
              {filtered.map((entry, i) => {
                const ext = fileExt(entry.file.name);
                const accent = getExtAccent(ext);
                const isImage = entry.file.type?.startsWith('image/') ?? false;
                const nameWithoutExt = entry.file.name.replace(/\.[^.]+$/, '');

                return (
                  <div
                    key={`${entry.file.url}-${i}`}
                    className={styles.fileCard}
                    onClick={() => handleClick(entry)}
                  >
                    <div className={styles.cardTop}>
                      <FileDocIcon ext={ext} accent={accent} isImage={isImage} url={entry.file.url} large />
                      <button className={styles.dlBtn} title={uiText("下载")} onClick={(e) => handleDownload(e, entry.file)}>
                        <svg width={15} height={15} viewBox="0 0 24 24" fill="none">
                          <path d="M12 4v11M7 11l5 5 5-5M5 20h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    </div>
                    <div className={styles.cardTitleGroup}>
                      <div className={styles.cardTitle}>
                        {nameWithoutExt}
                        <span className={styles.fileNameExt}>.{ext}</span>
                      </div>
                      <div className={styles.cardSize}>{formatFileSize(entry.file.size)}</div>
                    </div>
                    <div className={styles.cardBottom}>
                      <span className={styles.cardSender}>{entry.sender}</span>
                      <span className={styles.cardTime}>{formatTime(entry.timestamp)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
